import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MATFILE_ENDLESS_TYPE } from 'pxc-editor-core/constants';
import { colors } from '@/globalStyles';
import store from '@/store';
import { ACTIVE_COLOR_MODE, INACTIVE_COLOR_MODE, COLOR_MODE } from '@/constants';
import { updateSectionProps, handleShapeClick } from '@/actions';
import { SymbolEntity, ShapeEntity, isWebComponentEnvironment } from '@/helpers';
import { getBuildedMatfileTemplate } from '@/helpers/matfile';

const isBorderEnabled = (data) => {
  const { colorMode, color } = data;
  
  switch (colorMode) {
    case ACTIVE_COLOR_MODE.ENABLE_BORDER: {
      return color;
    }
    case ACTIVE_COLOR_MODE.DISABLE_BORDER: {
      return 'none';
    }
    case INACTIVE_COLOR_MODE.ARTICLE_COLOR_WITH_BORDER: {
      return color;
    }
    case INACTIVE_COLOR_MODE.ARTICLE_COLOR_WITHOUT_BORDER: {
      return 'none';
    }
    default: {
      return color;
    }
  }
};

const getStrokeColor = (data) => {
  const { shapeObj, outline_style, isSelected, ribbonColor } = data;
  const matfileTemplate = getBuildedMatfileTemplate();
  const isEndlessMaterial = matfileTemplate?.type === MATFILE_ENDLESS_TYPE;
  const color = isEndlessMaterial ? colors.black : ribbonColor;

  if (shapeObj?.active_color_mode && shapeObj?.inactive_color_mode) {
    if (isSelected) {
      return isBorderEnabled({ colorMode: shapeObj.active_color_mode, color });
    }
    return isBorderEnabled({ colorMode: shapeObj.inactive_color_mode, color });
  }

  if (outline_style?.enabled && outline_style?.color) {
    return outline_style.color;
  }

  return color;
};

const getFillColor = ({ primitiveProps, colorSchema, logicalType }) => {
  const { background_style } = primitiveProps;

  switch (background_style?.color_mode) {
    case COLOR_MODE.DEFINED_COLOR: {
      return background_style.fill_color;
    }
    case COLOR_MODE.VARIANT_COLOR: {
      if (colorSchema) {
        return colorSchema.background_color_hex;
      }
    }
    case COLOR_MODE.WITHOUT_COLOR:
    case COLOR_MODE.VARIANT_ELEMENT_COLOR_OR_DEFINED_COLOR:
    default: {
      if (logicalType === 'hole') {
        return colors.white;
      } else if (background_style?.enabled) {
        return background_style.fill_color;
      }
      return colors.transparent;
    }
  }
};

const RectComponent = props => {
  const { style, className, id, primitiveProps, withMiddleLines, shapeObj, logicalType, svgElementClickableRef } = props;
  const { x, y, w: width, h: height, roundedCornerRadius: borderRadius, angle, outline_style } = primitiveProps;
  const shieldsSelection = useSelector(state => state.core.shieldsSelection);
  const colorSchema = useSelector(state => state.core.colorSchema);
  const ribbonColor = useSelector(state => state.core.ribbonColor);
  const dispatch = useDispatch();
  const isSelected = !!shieldsSelection[shapeObj?.id];
  const midHorLineY = y + height / 2;
  const midVerLineX = x + width / 2;
  const parsedStyle = JSON.parse(JSON.stringify(style));
  const transform = { ...(!!angle && { transform: `rotate(${Math.abs(-90)} ${x} ${y + height})` }) };
  const styles = {
    ...parsedStyle,
    pointerEvents: className === 'decoration' ? 'none' : 'unset',
    ...(!parsedStyle.fill && { fill: getFillColor({ primitiveProps, colorSchema, logicalType }) }),
    stroke: getStrokeColor({ shapeObj, outline_style, isSelected, ribbonColor })
  };

  const handleDrop = e => {
    if (!isWebComponentEnvironment()) return;

    if (shapeObj) {
      dispatch(handleShapeClick(shapeObj));

      setTimeout(() => {
        const { core: { fictiveSections } } = store.getState();
        const selectedSection = Object.values(fictiveSections).find(item => item.shieldId === id);

        if (selectedSection) {
          if (!!SymbolEntity.symbol) {
            dispatch(updateSectionProps(selectedSection, SymbolEntity.symbol));
            SymbolEntity.setSymbol(null);
          } else if (ShapeEntity.shape) {
            dispatch(updateSectionProps(selectedSection, { shape: ShapeEntity.shape }));
            ShapeEntity.resetShape();
          }
        }
      }, 0);
    }
  };

  const onDragOver = e => {
    if (!isWebComponentEnvironment()) return;

    e.preventDefault();
  };

  return (
    <>
      <rect
        id={id}
        x={x}
        y={y}
        width={width}
        height={height}
        rx={borderRadius ? `${borderRadius}px` : 0}
        ry={borderRadius ? `${borderRadius}px` : 0}
        {...transform}
        style={styles}
        className={className}
        ref={svgElementClickableRef}
        onDrop={handleDrop}
        onDragOver={onDragOver}
      />
      {withMiddleLines && (
        <>
          <line x1={x} y1={midHorLineY} x2={x + width} y2={midHorLineY} stroke={style.stroke} strokeWidth={style.strokeWidth} />
          <line x1={midVerLineX} y1={y} x2={midVerLineX} y2={y + height} stroke={style.stroke} strokeWidth={style.strokeWidth} />
        </>
      )}
    </>
  );
};

export default RectComponent;
